import { Button, State } from '@/components/atoms';
import { DataTable } from '@/components/molecules';
import { useShippingMethods, useDeleteShippingMethod } from '@/api';
import type { ColumnDef } from '@tanstack/react-table';
import type { ShippingMethod } from '@/types';
import { Trash2, Pencil } from 'lucide-react';
import { formatCurrency } from '@/lib/helpers';

interface AllShippingMethodsProps {
  toggleOpen: () => void;
  setMode: (mode: 'update' | 'create') => void;
  setId: (id: number) => void;
}

export const AllShippingMethods = ({
  toggleOpen,
  setMode,
  setId,
}: AllShippingMethodsProps) => {
  const { data: shippingMethods } = useShippingMethods();
  const { mutate: remove } = useDeleteShippingMethod();

  const columns: ColumnDef<ShippingMethod>[] = [
    {
      accessorKey: 'location',
      header: 'Location',
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('location')}</div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => <div>{row.getValue('description')}</div>,
    },
    {
      accessorKey: 'fee',
      header: 'Shipping Fee',
      cell: ({ row }) => (
        <div className="font-medium">{formatCurrency(row.getValue('fee'))}</div>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Date',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          {new Date(row.getValue('createdAt')).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setId(row.original.id);
              setMode('update');
              toggleOpen();
            }}
            className="text-xs"
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              remove(row.original.id);
            }}
            className="text-xs text-red-500 hover:text-red-600"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      {shippingMethods && shippingMethods?.length < 1 ? (
        <State
          description="You don't have any shipping methods yet"
          button={{
            icon: 'Plus',
            text: 'Add Shipping Method',
            onClick: toggleOpen,
          }}
        />
      ) : (
        <DataTable columns={columns} data={shippingMethods || []} />
      )}
    </>
  );
};
